import { JsonObject, JsonValue } from '../app/json/jsonValue.js';

/**
 * Records ONE IAppSite handler invocation during applyDynamicAppSiteHandlers — the from/to AppSite/AppView,
 * whether it changed the request, and the handler-supplied reason token.
 *
 * Responsibilities:
 * - Preserve every intermediate navigation decision in a handler chain (not just the final pair), so a
 *   scenario can assert a specific hop, not only the endpoint.
 * - Expose a stable JSON shape (toJson) for the harness "navigation" state-validation source.
 */
export class NavigationDecision {
    /** The IAppSite handler type that produced this decision (e.g. "SetupBootstrapAppSite"). */
    handler: string;
    /** The AppSite entering this handler. */
    fromAppSite: string;
    /** The AppView entering this handler. */
    fromAppView: string;
    /** The AppSite this handler resolved to. */
    toAppSite: string;
    /** The AppView this handler resolved to. */
    toAppView: string;
    /** True when this handler changed the AppSite or AppView. */
    changed: boolean;
    /** Stable token describing why this handler resolved as it did (empty for a no-op). */
    reason: string;

    constructor(handler: string = '', fromAppSite: string = '', fromAppView: string = '', toAppSite: string = '', toAppView: string = '', changed: boolean = false, reason: string = '') {
        this.handler = handler || '';
        this.fromAppSite = fromAppSite || '';
        this.fromAppView = fromAppView || '';
        this.toAppSite = toAppSite || '';
        this.toAppView = toAppView || '';
        this.changed = changed;
        this.reason = reason || '';
    }

    /** Serializes this decision to the stable JSON shape used by the trace surface. */
    toJson(): JsonObject {
        const obj = new JsonObject();
        obj.set('Handler', JsonValue.string(this.handler));
        obj.set('FromAppSite', JsonValue.string(this.fromAppSite));
        obj.set('FromAppView', JsonValue.string(this.fromAppView));
        obj.set('ToAppSite', JsonValue.string(this.toAppSite));
        obj.set('ToAppView', JsonValue.string(this.toAppView));
        obj.set('Changed', JsonValue.bool(this.changed));
        obj.set('Reason', JsonValue.string(this.reason));
        return obj;
    }
}
